import React from 'react';
import Alert from '@amzn/awsui-components-console/alert';
import Badge from '@amzn/awsui-components-console/badge';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import Container from '@amzn/awsui-components-console/container';
import Popover from '@amzn/awsui-components-console/popover';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Spinner from '@amzn/awsui-components-console/spinner';
import Skeleton from '@amzn/awsui-components-console/skeleton';
import Table from '@amzn/awsui-components-console/table';
import { formatDayMonthYearTime } from 'src/common/utils/dateFormatters';
import { StringGroupVersionSummary, TranslationStatus } from '../types';
import {
  ROLLBACK_IN_PROGRESS_LOCK_REASON_KEY,
  SUBMISSION_PENDING_LOCK_REASON_KEY,
} from '../constants';
import styles from '../StringGroupDetail.module.scss';
import AccessDeniedAlert from 'src/common/authz/AccessDeniedAlert';
import {
  getAccessDeniedMessage,
  getAccessDeniedNamespace,
  isAccessDenied,
} from 'src/common/authz/isAccessDenied';
import { useTranslation } from 'src/i18n/shim/useTranslation';
import { VERSION_HISTORY_WINDOW } from '../hooks/useVersionHistory';

const NOT_AVAILABLE = '—';

/**
 * Width of the Actions column, wide enough to keep Diff, View and Rollback on
 * one line for a middle version (three buttons). Mirrors the string detail
 * page's version-history table, which fixes its actions-column width for the
 * same reason.
 */
const ACTIONS_COLUMN_WIDTH = 340;

interface VersionHistoryPanelProps {
  visible: boolean;
  stringGroupName: string;
  versions: StringGroupVersionSummary[] | null;
  loading: boolean;
  error?: Error | null;
  /**
   * When false, the Rollback action is disabled (grayed out) because the group
   * is owned by a non-LangHub client. This is one of several conditions gating
   * rollback — translation-in-progress and submission-pending also disable it.
   * Diff/View stay available (read-only).
   */
  canEdit?: boolean;
  /**
   * Whether the permission answer is still in flight. Gated controls show a skeleton rather
   * than a disabled state, so they never claim a missing permission before it is known.
   */
  permissionsLoading?: boolean;
  /**
   * Current translation status of the string group's latest version. When
   * `InProgress`, rollback is blocked because it would create a competing
   * submission.
   */
  translationStatus?: TranslationStatus;
  /**
   * When true, a submission is awaiting TRANSCOM confirmation. Rollback is
   * locked with a visible reason on hover — the group is frozen until the
   * in-flight submission is confirmed.
   */
  submissionPending?: boolean;
  onClose: () => void;
  onDiff: (version: number) => void;
  onView: (version: number) => void;
  onRollback: (version: number) => void;
}

// Uses the shared local-timezone timestamp formatter so this date reads the
// same as the string detail page's version-history table.
function formatDate(locale: string, timestamp: number): string {
  if (!timestamp) {
    return NOT_AVAILABLE;
  }
  return formatDayMonthYearTime(locale, new Date(timestamp));
}

const VersionHistoryPanel: React.FC<VersionHistoryPanelProps> = ({
  visible,
  stringGroupName,
  versions,
  loading,
  error,
  canEdit = true,
  permissionsLoading = false,
  translationStatus,
  submissionPending = false,
  onClose,
  onDiff,
  onView,
  onRollback,
}) => {
  const { t, locale } = useTranslation();
  if (!visible) {
    return null;
  }

  const latestVersion =
    versions && versions.length > 0
      ? Math.max(...versions.map((v) => v.version))
      : 0;

  const isInProgress = translationStatus === TranslationStatus.InProgress;
  const rollbackDisabled = !canEdit || isInProgress || submissionPending;

  // Determine the Popover reason when the button is disabled for a
  // non-permission reason that the user can act on. Permission denial
  // (!canEdit alone) shows no popover — matching the rest of the gated
  // controls.
  const rollbackDisabledReason = submissionPending
    ? t(SUBMISSION_PENDING_LOCK_REASON_KEY)
    : isInProgress
      ? t(ROLLBACK_IN_PROGRESS_LOCK_REASON_KEY)
      : undefined;

  const renderRollbackButton = (vNum: number, version: number) => {
    if (permissionsLoading) {
      return <Skeleton display="inline-block" height="32px" width="85px" />;
    }

    const button = (
      <Button
        variant="normal"
        disabled={rollbackDisabled}
        onClick={() => onRollback(vNum)}
        ariaLabel={t('sgd-rollback-to-version-aria', { version })}
      >
        {t('sgd-rollback')}
      </Button>
    );

    // When disabled for an actionable reason (not just !canEdit), show why.
    if (rollbackDisabledReason && canEdit) {
      return (
        <Popover
          content={rollbackDisabledReason}
          dismissButton={false}
          position="top"
          size="medium"
          triggerType="custom"
        >
          {button}
        </Popover>
      );
    }

    return button;
  };

  return (
    <Container
      header={
        <div className={styles.versionHistoryHeader}>
          <h3 className={styles.versionHistoryTitle}>
            {t('sgd-version-history-header', { group: stringGroupName })}
          </h3>
          <Button
            variant="normal"
            onClick={onClose}
            ariaLabel={t('sgd-version-history-close')}
          >
            {t('sgd-close-with-mark')}
          </Button>
        </div>
      }
    >
      <div className={styles.versionHistorySubtitle}>
        {t('sgd-version-window-notice', { count: VERSION_HISTORY_WINDOW })}
      </div>

      {loading && <Spinner size="normal" />}

      {/*
        A refusal shows the service's own wording alone: the "Failed to load
        version history:" prefix would be describing a fault, and prefixing
        service copy with UI copy is how the two drift apart.
      */}
      {!loading &&
        error &&
        (isAccessDenied(error) ? (
          <AccessDeniedAlert
            message={getAccessDeniedMessage(error)}
            deniedNamespace={getAccessDeniedNamespace(error)}
            data-testid="version-history-access-denied"
          />
        ) : (
          <Alert type="error">
            {t('sgd-version-history-load-failed', { detail: error.message })}
          </Alert>
        ))}

      {!loading && !error && versions?.length === 0 && (
        <Box textAlign="center" color="text-body-secondary" padding="l">
          {t('sgd-version-empty')}
        </Box>
      )}

      {!loading && !error && versions && versions.length > 0 && (
        <Table
          items={versions}
          ariaLabels={{ tableLabel: t('sgd-version-history-table-aria') }}
          columnDefinitions={[
            {
              id: 'version',
              header: t('string-column-version'),
              cell: (v) => (
                <span
                  aria-label={t('sgd-version-label', { version: v.version })}
                >
                  <Box fontWeight="bold">
                    {t('version-label-short', { version: v.version })}
                  </Box>
                </span>
              ),
              width: 100,
            },
            {
              id: 'date',
              header: t('string-column-date'),
              cell: (v) => (
                <Box variant="p">{formatDate(locale, v.updatedOn)}</Box>
              ),
              width: 130,
            },
            {
              id: 'change',
              header: t('string-column-change'),
              cell: (v) => (
                <SpaceBetween size="xxs">
                  <Box fontWeight="bold">
                    {v.changeSummary?.description ??
                      t('sgd-version-label', { version: v.version })}
                    {v.translationStatus === TranslationStatus.Canceled && (
                      <>
                        {' '}
                        <Badge color="grey">
                          {t('sgd-progress-cancelled')}
                        </Badge>
                      </>
                    )}
                  </Box>
                  <Box variant="small" color="text-status-inactive">
                    {t('string-version-by-author', { author: v.updatedBy })}
                  </Box>
                  {v.translationStatus === TranslationStatus.Canceled &&
                    v.cancelRequestedBy && (
                      <Box variant="small" color="text-status-inactive">
                        {/* Two whole sentences, not one plus an appended
                            " on {date}": the date's position differs by
                            language, and a trailing fragment cannot move. */}
                        {v.cancelRequestedOn
                          ? t('sgd-version-cancel-requested-by-on', {
                              user: v.cancelRequestedBy,
                              date: formatDate(locale, v.cancelRequestedOn),
                            })
                          : t('sgd-version-cancel-requested-by', {
                              user: v.cancelRequestedBy,
                            })}
                      </Box>
                    )}
                </SpaceBetween>
              ),
            },
            {
              id: 'actions',
              header: t('string-column-actions'),
              cell: (v) => {
                const vNum = v.version;
                return (
                  <div className={styles.versionActions}>
                    {/* Diff needs a predecessor v(n-1); v1 has none, so the
                        DiffModal's `previousVersion < 1` guard would load
                        nothing. */}
                    {vNum !== 1 && (
                      <Button
                        variant="normal"
                        onClick={() => onDiff(vNum)}
                        ariaLabel={t('string-version-diff-aria', {
                          version: v.version,
                        })}
                      >
                        {t('string-version-diff')}
                      </Button>
                    )}
                    {/* View and Rollback are meaningful for any version that is
                        not the current one: View shows a historical snapshot
                        (the latest is already on the page), and Rollback
                        restores that version's content — including v1, which
                        only needs its own snapshot, not a predecessor. */}
                    {vNum !== latestVersion && (
                      <>
                        <Button
                          variant="normal"
                          onClick={() => onView(vNum)}
                          ariaLabel={t('string-version-view-aria', {
                            version: v.version,
                          })}
                        >
                          {t('string-version-view')}
                        </Button>
                        {renderRollbackButton(vNum, v.version)}
                      </>
                    )}
                  </div>
                );
              },
              width: ACTIONS_COLUMN_WIDTH,
              minWidth: ACTIONS_COLUMN_WIDTH,
            },
          ]}
        />
      )}
    </Container>
  );
};

export default VersionHistoryPanel;
